export interface SessionState {
  csrf: string;
  user: { id: string; email: string; name: string | null; verified: boolean } | null;
  entitlement: { plan: "guest" | "all_access"; active: boolean };
  features?: { email_auth: boolean; google_auth: boolean; billing: boolean };
}

export interface QuotaResponse {
  allowed: boolean;
  remaining: number | null;
  plan: "guest" | "all_access";
}

export class AccountClient {
  private sessionPromise: Promise<SessionState> | null = null;

  constructor(private readonly apiOrigin: string) {}

  session(): Promise<SessionState> {
    this.sessionPromise ??= this.request<SessionState>("/v1/session");
    return this.sessionPromise;
  }

  async consumeQuota(deviceId: string): Promise<QuotaResponse> {
    const session = await this.session();
    return this.request<QuotaResponse>("/v1/quota/consume", {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrf },
      body: JSON.stringify({ device_id: deviceId }),
    });
  }

  async register(email: string, password: string, name: string): Promise<SessionState> {
    await this.authPost("/v1/auth/register", { email, password, name });
    return this.reloadSession();
  }

  async login(email: string, password: string): Promise<SessionState> {
    await this.authPost("/v1/auth/login", { email, password });
    return this.reloadSession();
  }

  async logout(): Promise<SessionState> {
    await this.authPost("/v1/auth/logout", {});
    return this.reloadSession();
  }

  async checkout(): Promise<string> {
    const response = await this.authPost<{ url: string }>("/v1/billing/checkout", {});
    return response.url;
  }

  private async authPost<T = { ok: boolean }>(path: string, body: object): Promise<T> {
    const session = await this.session();
    return this.request<T>(path, {
      method: "POST",
      headers: { "Content-Type": "application/json", "X-CSRF-Token": session.csrf },
      body: JSON.stringify(body),
    });
  }

  private reloadSession(): Promise<SessionState> {
    this.sessionPromise = null;
    return this.session();
  }

  private async request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const response = await fetch(`${this.apiOrigin}${path}`, { ...init, credentials: "include", cache: "no-store" });
    const body = await response.json().catch(() => ({})) as T & { error?: string };
    if (response.status === 429 && typeof body === "object" && body !== null && "allowed" in body) return body;
    if (!response.ok) throw new Error(body.error ?? `TikVN account service returned ${response.status}`);
    return body;
  }
}
